
'use client';

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { PeriodicoArticle, CalendarEvent } from '@/lib/types';
import Image from '@/components/ui/Image';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  ScrollText, Calendar, Newspaper, Mail, TrendingUp, Users, Landmark,
  Music, Sword, ChevronRight, Star, Bell, Zap, BookOpen,
  Clock, MessageSquare, Church,
} from 'lucide-react';
import { ServerStatus } from './ServerStatus';
import { ProcesionEnDirecto } from '@/components/cecop/ProcesionEnDirecto';
import { useAuth } from '@/hooks/useAuth';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

// ─── Greeting ────────────────────────────────────────────────────────────────

function getGreeting() {
  const h = new Date().getHours();
  if (h < 6) return 'Buenas noches';
  if (h < 14) return 'Buenos días';
  if (h < 21) return 'Buenas tardes';
  return 'Buenas noches';
}

// ─── Count-up animation ───────────────────────────────────────────────────────

function CountUp({ to, duration = 1200 }: { to: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const raf = useRef<number | null>(null);
  useEffect(() => {
    const start = performance.now();
    const step = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      setCount(Math.round(p * to));
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [to, duration]);
  return <>{count}</>;
}

// ─── Quick action card ────────────────────────────────────────────────────────

const ACTION_ITEMS = [
  { icon: Mail, label: 'Buzón', to: '/jartinet/buzon', color: 'text-primary', bg: 'bg-primary/10 hover:bg-primary/20 border-primary/20' },
  { icon: MessageSquare, label: 'Social', to: '/jartinet/social', color: 'text-violet-400', bg: 'bg-violet-500/10 hover:bg-violet-500/20 border-violet-500/20' },
  { icon: Landmark, label: 'JartiBank', to: '/jartinet/banco', color: 'text-amber-400', bg: 'bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/20' },
  { icon: Users, label: 'Hermandades', to: '/jartinet/hermandades', color: 'text-blue-400', bg: 'bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/20' },
  { icon: Music, label: 'Bandas', to: '/jartinet/bandas', color: 'text-green-400', bg: 'bg-green-500/10 hover:bg-green-500/20 border-green-500/20' },
  { icon: Calendar, label: 'Calendario', to: '/jartinet/calendario', color: 'text-orange-400', bg: 'bg-orange-500/10 hover:bg-orange-500/20 border-orange-500/20' },
  { icon: Sword, label: 'Programa SS', to: '/jartinet/programa-semana-santa', color: 'text-red-400', bg: 'bg-red-500/10 hover:bg-red-500/20 border-red-500/20' },
] as const;

// ─── Event pill ───────────────────────────────────────────────────────────────

const EventPill = ({ event, index }: { event: CalendarEvent; index: number }) => {
  const start = new Date(event.start);
  const isToday = new Date().toDateString() === start.toDateString();
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.08 }}
      className={cn(
        'flex items-center gap-4 p-3 rounded-2xl border transition-all group',
        isToday
          ? 'bg-primary/8 border-primary/25 hover:border-primary/50'
          : 'bg-white/3 border-white/8 hover:border-white/20',
      )}
    >
      <div className={cn(
        'flex flex-col items-center justify-center w-11 h-11 rounded-xl shrink-0 font-bold',
        isToday ? 'bg-primary text-primary-foreground' : 'bg-white/8',
      )}>
        <span className="text-sm leading-none">{format(start, 'dd')}</span>
        <span className="text-[0.6rem] uppercase leading-none mt-0.5 opacity-75">{format(start, 'MMM', { locale: es })}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm truncate">{event.title}</p>
        <p className="text-xs text-muted-foreground">
          {isToday ? '¡Hoy!' : formatDistanceToNowStrict(start, { addSuffix: true, locale: es })}
        </p>
      </div>
      {isToday && <div className="h-2 w-2 rounded-full bg-primary shrink-0 animate-pulse" />}
    </motion.div>
  );
};

// ─── Main export ──────────────────────────────────────────────────────────────

// ─── Tarjeta de procesión ─────────────────────────────────────────────────────

const ProcesionCard = ({ event, index }: { event: CalendarEvent; index: number }) => {
  const start = new Date(event.start);
  const end = new Date(event.end);
  const color = event.color || 'hsl(var(--primary))';
  const isToday = new Date().toDateString() === start.toDateString();
  const horario = event.allDay
    ? 'Todo el día'
    : `${format(start, 'HH:mm')} – ${format(end, 'HH:mm')}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className="relative overflow-hidden rounded-2xl border border-white/8 bg-white/[0.03] p-4 transition-all hover:border-white/20"
    >
      <span className="absolute inset-y-0 left-0 w-1.5" style={{ backgroundColor: color }} />
      <div className="flex items-center gap-3 pl-2">
        <Church className="h-5 w-5 shrink-0" style={{ color }} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-bold leading-tight" style={{ color }}>{event.title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            <span className="capitalize">{format(start, "eeee d 'de' MMMM", { locale: es })}</span> · {horario}
          </p>
        </div>
        <span className={cn(
          'shrink-0 rounded-full px-2.5 py-1 text-[0.7rem] font-semibold',
          isToday ? 'animate-pulse bg-primary text-primary-foreground' : 'bg-white/8 text-muted-foreground',
        )}>
          {isToday ? '¡Hoy!' : formatDistanceToNowStrict(start, { addSuffix: true, locale: es })}
        </span>
      </div>
    </motion.div>
  );
};

// ─── Main export ──────────────────────────────────────────────────────────────

interface JartinetHomePageClientProps {
  upcomingEvents: CalendarEvent[];
  upcomingProcesiones?: CalendarEvent[];
  latestArticle: PeriodicoArticle | null;
  error: string | null;
}

export function JartinetHomePageClient({ upcomingEvents, upcomingProcesiones = [], latestArticle, error }: JartinetHomePageClientProps) {
  const { userData, jartinetData } = useAuth();
  const [showRules, setShowRules] = useState(false);

  const hermandadesCount = jartinetData?.hermandades?.length ?? 0;
  const bandasCount = jartinetData?.bandas?.length ?? 0;
  const greeting = getGreeting();
  const nickname = userData?.nickname || 'Hermano';

  const pdfUrl = 'https://drive.google.com/file/d/1bKxkCjaSA2K7C9Yo6qjVStfik8HnUYOu/preview';

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <h2 className="text-xl font-semibold text-destructive">Error al cargar el panel</h2>
        <p className="text-muted-foreground mt-2">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Greeting hero ── */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-primary/18 via-primary/6 to-transparent border border-primary/15 p-6"
      >
        {/* Decorative glow */}
        <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-primary/10 blur-2xl pointer-events-none" />

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-sm text-muted-foreground font-medium"
            >
              {greeting},
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl font-black"
            >
              {nickname}
              <span className="text-primary"> ✦</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45 }}
              className="text-sm text-muted-foreground mt-1"
            >
              {format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
            </motion.p>
          </div>

          {/* Stats chips */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap gap-2"
          >
            {[
              { label: 'hermandad' + (hermandadesCount !== 1 ? 'es' : ''), value: hermandadesCount, icon: Users },
              { label: 'banda' + (bandasCount !== 1 ? 's' : ''), value: bandasCount, icon: Music },
              { label: 'evento' + (upcomingEvents.length !== 1 ? 's' : ''), value: upcomingEvents.length, icon: Calendar },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/12 text-sm">
                <Icon className="h-3.5 w-3.5 text-primary" />
                <span className="font-bold tabular-nums"><CountUp to={value} /></span>
                <span className="text-muted-foreground text-xs">{label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* ── Procesión en directo (CECOP) — solo aparece si hay una en curso ── */}
      <ProcesionEnDirecto />

      {/* ── Server status ── */}
      <ServerStatus ip="jartiland.servidor.lol" />

      {/* ── Quick actions ── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Zap className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Accesos rápidos</h2>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2.5">
          {ACTION_ITEMS.map(({ icon: Icon, label, to, color, bg }, i) => (
            <motion.div
              key={to}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i }}
            >
              <Link
                to={to}
                className={cn(
                  'flex flex-col items-center gap-1.5 p-3 rounded-2xl border transition-all group',
                  bg,
                )}
              >
                <div className={cn('p-2 rounded-xl transition-transform group-hover:scale-110', color)}>
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-[0.6rem] font-semibold text-muted-foreground group-hover:text-foreground transition-colors text-center leading-tight">{label}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Próximas procesiones ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Church className="h-4 w-4 text-primary" />
            <h2 className="font-bold">Próximas procesiones</h2>
          </div>
          <Button variant="ghost" size="sm" asChild className="text-xs h-7">
            <Link to="/jartinet/calendario">Ver calendario <ChevronRight className="h-3 w-3 ml-1" /></Link>
          </Button>
        </div>

        {upcomingProcesiones.length > 0
          ? <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {upcomingProcesiones.map((e, i) => <ProcesionCard key={e.id} event={e} index={i} />)}
            </div>
          : <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-white/10 py-8 text-muted-foreground">
              <Church className="mb-2 h-8 w-8 opacity-30" />
              <p className="text-sm">No hay procesiones próximas</p>
            </div>
        }
      </div>

      {/* ── Main grid: events + news + rules ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

        {/* Events */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <h2 className="font-bold">Próximos eventos</h2>
            </div>
            <Button variant="ghost" size="sm" asChild className="text-xs h-7">
              <Link to="/jartinet/calendario">Ver todos <ChevronRight className="h-3 w-3 ml-1" /></Link>
            </Button>
          </div>

          {upcomingEvents.length > 0
            ? <div className="space-y-2">
                {upcomingEvents.map((e, i) => <EventPill key={e.id} event={e} index={i} />)}
              </div>
            : <div className="flex flex-col items-center justify-center py-10 rounded-2xl border border-dashed border-white/10 text-muted-foreground">
                <Calendar className="h-8 w-8 mb-2 opacity-30" />
                <p className="text-sm">No hay eventos próximos</p>
              </div>
          }
        </div>

        {/* Latest article */}
        <div className="lg:col-span-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Newspaper className="h-4 w-4 text-primary" />
              <h2 className="font-bold">Última noticia</h2>
            </div>
            <Button variant="ghost" size="sm" asChild className="text-xs h-7">
              <Link to="/jartinet/periodico">Periódico <ChevronRight className="h-3 w-3 ml-1" /></Link>
            </Button>
          </div>

          {latestArticle ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-3xl border border-white/8 bg-card/40 overflow-hidden group hover:border-white/20 transition-all"
            >
              <div className="relative w-full aspect-video overflow-hidden">
                <Image src={latestArticle.imageUrl} alt={latestArticle.title} fill className="object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="font-bold text-white text-lg line-clamp-2 leading-snug">{latestArticle.title}</h3>
                  <p className="text-white/70 text-xs mt-1">Por {latestArticle.authorName}</p>
                </div>
              </div>
              <div className="p-4 flex justify-end">
                <Button size="sm" asChild className="rounded-xl">
                  <Link to="/jartinet/periodico">Leer más <ChevronRight className="h-3.5 w-3.5 ml-1" /></Link>
                </Button>
              </div>
            </motion.div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 rounded-2xl border border-dashed border-white/10 text-muted-foreground">
              <Newspaper className="h-8 w-8 mb-2 opacity-30" />
              <p className="text-sm">No hay artículos recientes</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Normativas (colapsable) ── */}
      <div className="rounded-3xl border border-white/8 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowRules(v => !v)}
          className="w-full flex items-center gap-3 px-5 py-4 hover:bg-white/4 transition-colors text-left"
        >
          <div className="p-2 rounded-xl bg-primary/10">
            <ScrollText className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-semibold">Normativas del Servidor</p>
            <p className="text-xs text-muted-foreground">Consulta el reglamento de Jartiland</p>
          </div>
          <BookOpen className={cn('h-4 w-4 text-muted-foreground transition-transform', showRules && 'rotate-180')} />
        </button>

        <AnimatePresence initial={false}>
          {showRules && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden border-t border-white/8"
            >
              <div className="aspect-[3/4] max-h-[700px]">
                <iframe
                  src={pdfUrl}
                  className="w-full h-full border-0"
                  title="Normativas de Jartiland"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

    </div>
  );
}
